import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { RiShieldCheckLine, RiKeyLine } from '@remixicon/react';
import { Button } from '@marine/ui/button';
import { Input } from '@marine/ui/input';
import { Label } from '@marine/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@marine/ui/card';
import { api } from '@marine/api-client/api';
import { sanitizeSvg } from '@/lib/sanitizeHtml';
import { toast } from 'sonner';

export function TwoFactorCard() {
  const [confirmationCode, setConfirmationCode] = useState('');
  const [showRecoveryCodes, setShowRecoveryCodes] = useState(false);
  const queryClient = useQueryClient();

  const { data: user } = useQuery({
    queryKey: ['auth-user'],
    queryFn: () => api.getOne<{ two_factor_confirmed_at?: string }>('/api/user'),
  });

  const is2FAEnabled = !!user?.two_factor_confirmed_at;

  // Enable 2FA
  const enable2FA = useMutation({
    mutationFn: () => api.post('/api/user/two-factor-authentication'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['auth-user'] });
      queryClient.invalidateQueries({ queryKey: ['2fa-qr'] });
      toast.success('2FA enabled. Scan the QR code with your authenticator app.');
    },
  });

  // Get QR Code
  const { data: qrCode } = useQuery({
    queryKey: ['2fa-qr'],
    queryFn: () => api.getOne<{ svg: string }>('/api/user/two-factor-qr-code'),
    enabled: !is2FAEnabled && enable2FA.isSuccess,
  });

  // Confirm 2FA
  const confirm2FA = useMutation({
    mutationFn: (code: string) => 
      api.post('/api/user/confirmed-two-factor-authentication', { code }),
    onSuccess: () => {
      toast.success('2FA confirmed successfully');
      queryClient.invalidateQueries({ queryKey: ['auth-user'] });
      setConfirmationCode('');
      setShowRecoveryCodes(true);
    },
    onError: () => {
      toast.error('Invalid confirmation code');
    },
  });

  // Get Recovery Codes
  const { data: recoveryCodes } = useQuery({
    queryKey: ['2fa-recovery'],
    queryFn: () => api.getList<string>('/api/user/two-factor-recovery-codes'),
    enabled: is2FAEnabled,
  });

  // Disable 2FA
  const disable2FA = useMutation({
    mutationFn: () => api.delete('/api/user/two-factor-authentication'),
    onSuccess: () => {
      toast.success('2FA disabled');
      queryClient.invalidateQueries({ queryKey: ['auth-user'] });
      setShowRecoveryCodes(false);
    },
  });

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <RiShieldCheckLine className="h-5 w-5 text-muted-foreground" />
          <div>
            <CardTitle>Two-Factor Authentication</CardTitle>
            <CardDescription>
              Add an extra layer of security to your account
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {!is2FAEnabled ? (
          <>
            {!enable2FA.isSuccess ? (
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  Two-factor authentication is not enabled. Enable it to secure your account.
                </p>
                <Button 
                  onClick={() => enable2FA.mutate()}
                  disabled={enable2FA.isPending}
                >
                  Enable 2FA
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  Scan this QR code with your authenticator app (Google Authenticator, Authy, etc.)
                </p>
                
                {qrCode?.svg && (
                  <div className="flex justify-center p-4 bg-white rounded-lg">
                    <div dangerouslySetInnerHTML={{ __html: sanitizeSvg(qrCode.svg) }} />
                  </div>
                )}
                
                <div className="space-y-2">
                  <Label htmlFor="code">Enter Confirmation Code</Label>
                  <Input
                    id="code"
                    type="text"
                    placeholder="123456"
                    value={confirmationCode}
                    onChange={(e) => setConfirmationCode(e.target.value)}
                    maxLength={6}
                  />
                </div>
                
                <Button 
                  onClick={() => confirm2FA.mutate(confirmationCode)}
                  disabled={confirm2FA.isPending || confirmationCode.length !== 6}
                >
                  Confirm 2FA
                </Button>
              </div>
            )}
          </>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-green-600 dark:text-green-400">
              <RiShieldCheckLine className="h-5 w-5" />
              <span className="font-medium">Two-factor authentication is enabled</span>
            </div>
            
            {showRecoveryCodes && recoveryCodes && (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <RiKeyLine className="h-4 w-4" />
                  <Label>Recovery Codes</Label>
                </div>
                <p className="text-sm text-muted-foreground">
                  Store these codes securely. You can use them to access your account if you lose your device.
                </p>
                <div className="bg-muted p-4 rounded-lg font-mono text-sm grid grid-cols-2 gap-2">
                  {recoveryCodes.map((code: string, i: number) => (
                    <div key={i}>{code}</div>
                  ))}
                </div>
              </div>
            )}
            
            <Button 
              variant="destructive"
              onClick={() => disable2FA.mutate()}
              disabled={disable2FA.isPending}
            >
              Disable 2FA
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
